"use client";

import React from "react";
import { ArrowRight, Clock, Scale, Scissors, ShieldCheck, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface BreedItem {
  id: string;
  name: string;
  scientificGroup: string;
  size: string;
  temperament: string;
  grooming: string;
  lifespan: string;
  summary: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  to: string;
}
const breeds: BreedItem[] = [{
  id: "maine-coon",
  name: "Maine Coon",
  scientificGroup: "Natural Longhair Foundation",
  size: "Large (12–18 lbs)",
  temperament: "Gentle, sociable, and inquisitive",
  grooming: "High (Daily comb required)",
  lifespan: "12–15 years",
  summary: "Known for substantial bone density, heavy water-resistant double coats, and an unusually calm demeanor around families.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/maine_coon_cat_profile.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/maine_coon_cat_profile.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/maine_coon_cat_profile.png-wvc-sizes",
  imageAlt: "Adult Maine Coon cat sitting serenely with plush coat detail",
  to: "/BreedsPage"
}, {
  id: "siamese",
  name: "Siamese",
  scientificGroup: "Oriental Svelte Foundation",
  size: "Medium (7–12 lbs)",
  temperament: "Vocal, affectionate, and alert",
  grooming: "Low (Weekly soft brushing)",
  lifespan: "15–20 years",
  summary: "Recognized by striking point coloration, vivid blue almond eyes, and deep social engagement requiring frequent mental stimulation.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/siamese_cat_profile.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/siamese_cat_profile.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/siamese_cat_profile.webp-wvc-sizes",
  imageAlt: "Seal point Siamese cat resting in natural light",
  to: "/BreedsPage"
}, {
  id: "british-shorthair",
  name: "British Shorthair",
  scientificGroup: "Compact Semi-Cobby Profile",
  size: "Medium to Large (9–17 lbs)",
  temperament: "Placid, independent, and quiet",
  grooming: "Moderate (Bi-weekly brushing)",
  lifespan: "12–17 years",
  summary: "Features a dense plush coat, rounded facial contours, and an easygoing temperament suited for quiet households.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/british_shorthair_cat_profile.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/british_shorthair_cat_profile.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/british_shorthair_cat_profile.png-wvc-sizes",
  imageAlt: "Classic blue British Shorthair cat with amber eyes",
  to: "/BreedsPage"
}, {
  id: "ragdoll",
  name: "Ragdoll",
  scientificGroup: "Pointed Semi-Longhair",
  size: "Large (10–20 lbs)",
  temperament: "Docile, relaxed, and receptive",
  grooming: "Moderate to High (Regular care)",
  lifespan: "13–18 years",
  summary: "Distinguished by a silky non-matting plush coat, placid handling tolerance, and a strong preference for floor-level companionship.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/ragdoll_cat_profile.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/ragdoll_cat_profile.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/ragdoll_cat_profile.png-wvc-sizes",
  imageAlt: "Fluffy bicolor Ragdoll cat relaxing in a sunlit living room",
  to: "/BreedsPage"
}];
export default function CatBreeds() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="cat-breeds-directory" data-nav="light" className="bg-background py-16 sm:py-20 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Editorial Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border text-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <ShieldCheck className="size-3.5 text-foreground" />
            <span>Feline Directory</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground text-balance">
            Feline breed profiles
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Review physical attributes, behavioral tendencies, grooming requirements, and lifespan expectations verified through clinical standards.
          </p>
        </div>

        {/* Directory Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {breeds.map((breed, i) => <motion.div key={breed.id} variants={itemVariants} data-index={i}>
              <Card className="h-full flex flex-col justify-between overflow-hidden rounded-2xl border border-border bg-card transition-[border-color,box-shadow] duration-300 hover:border-border hover:shadow-md">
                <div>
                  {/* Photo Header */}
                  <div className="relative aspect-16/10 w-full overflow-hidden bg-muted border-b border-border">
                    <img src={breed.imageSrc} data-wvc-srcset={breed.imageSrcset} data-wvc-sizes={breed.imageSizes} alt={breed.imageAlt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                    <div className="absolute top-3 left-3 bg-background/90 backdrop-blur-xs border border-border px-3 py-1 rounded-md text-xs font-medium text-foreground">
                      {breed.scientificGroup}
                    </div>
                  </div>

                  {/* Header Titles */}
                  <CardHeader className="pt-6 pb-2">
                    <div className="flex items-baseline justify-between gap-2">
                      <CardTitle className="font-serif text-2xl font-semibold text-card-foreground">
                        {breed.name}
                      </CardTitle>
                      <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Clock className="size-3 text-muted-foreground" />
                        {breed.lifespan}
                      </span>
                    </div>
                    <CardDescription className="text-sm text-muted-foreground mt-1.5 line-clamp-2">
                      {breed.summary}
                    </CardDescription>
                  </CardHeader>

                  {/* Structured Clinical Attributes */}
                  <CardContent className="pt-3 pb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-3 border-t border-border">
                      <div className="p-2.5 rounded-lg bg-background/80 border border-border/80">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium mb-1">
                          <Scale className="size-3 text-foreground" />
                          <span>Size</span>
                        </div>
                        <p className="text-xs font-semibold text-card-foreground truncate">
                          {breed.size}
                        </p>
                      </div>

                      <div className="p-2.5 rounded-lg bg-background/80 border border-border/80">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium mb-1">
                          <Sparkles className="size-3 text-foreground" />
                          <span>Temperament</span>
                        </div>
                        <p className="text-xs font-semibold text-card-foreground truncate" title={breed.temperament}>
                          {breed.temperament}
                        </p>
                      </div>

                      <div className="p-2.5 rounded-lg bg-background/80 border border-border/80">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium mb-1">
                          <Scissors className="size-3 text-foreground" />
                          <span>Grooming</span>
                        </div>
                        <p className="text-xs font-semibold text-card-foreground truncate" title={breed.grooming}>
                          {breed.grooming}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </div>

                {/* Footer Action */}
                <CardFooter className="pt-2 pb-6 flex items-center justify-between border-t border-border/60">
                  <span className="text-xs text-muted-foreground font-medium">
                    Reviewed against clinical breed registries
                  </span>
                  <Button variant="outline" size="sm" asChild className="font-semibold text-foreground border-border hover:bg-secondary">
                    <Link to={breed.to} className="flex items-center gap-1.5">
                      <span>Full documentation</span>
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Footnote Governance Callout */}
        <div className="mt-12 p-5 sm:p-6 rounded-xl bg-card border-l-4 border-l-primary border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h4 className="text-sm font-semibold text-card-foreground">
              Clinical guidance note
            </h4>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-2xl">
              Individual temperament and health factors vary by lineage and environment. Always consult a licensed veterinarian for specialized breed health screenings.
            </p>
          </div>
          <Button variant="default" size="sm" asChild className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/BreedsPage">Browse all profiles</Link>
          </Button>
        </div>
      </div>
    </section>;
}